import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { PageSize } from '../_components/PDFDownloadModal';

interface PDFGeneratorOptions {
  elementId: string;
  fileName: string;
  pageSize: PageSize;
  quality: 'standard' | 'high';
  includeBleed: boolean;
  removeBackground?: boolean;
  removeBorder?: boolean;
}

export const generateInvitationPDF = async ({
  elementId,
  fileName,
  pageSize,
  quality,
  includeBleed,
}: PDFGeneratorOptions): Promise<void> => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found');
  }

  // Set DPI based on quality
  const dpi = quality === 'high' ? 300 : 150;
  const scale = dpi / 96; // 96 is standard screen DPI

  // Add bleed if requested (3mm or 0.125 inches)
  const bleedSize = includeBleed ? (pageSize.unit === 'mm' ? 3 : 0.125) : 0;

  try {
    // Wait for any animations to complete
    await new Promise(resolve => setTimeout(resolve, 100));

    // Simply capture the element as it is now (with correct dimensions)
    const canvas = await html2canvas(element, {
      scale: scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
      foreignObjectRendering: false,
    });

    // Create PDF
    const orientation = pageSize.width > pageSize.height ? 'landscape' : 'portrait';
    const pdf = new jsPDF({
      orientation,
      unit: pageSize.unit,
      format: [
        pageSize.width + (includeBleed ? 2 * bleedSize : 0),
        pageSize.height + (includeBleed ? 2 * bleedSize : 0)
      ],
    });

    // Get canvas dimensions in pixels
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // Calculate the aspect ratios
    const imgAspectRatio = imgWidth / imgHeight;
    
    let finalWidth: number;
    let finalHeight: number;
    let xOffset: number;
    let yOffset: number;
    
    if (includeBleed) {
      // When including bleed, size the invitation to fit within the bleed area
      const contentWidth = pageSize.width;
      const contentHeight = pageSize.height;
      const contentAspectRatio = contentWidth / contentHeight;
      
      // Scale to fit within the content area (inside bleed marks)
      if (imgAspectRatio > contentAspectRatio) {
        // Image is wider - scale by width
        finalWidth = contentWidth;
        finalHeight = contentWidth / imgAspectRatio;
        xOffset = bleedSize;
        yOffset = bleedSize + (contentHeight - finalHeight) / 2;
      } else {
        // Image is taller - scale by height
        finalHeight = contentHeight;
        finalWidth = contentHeight * imgAspectRatio;
        xOffset = bleedSize + (contentWidth - finalWidth) / 2;
        yOffset = bleedSize;
      }
    } else {
      // Without bleed, fill the entire page
      const targetPageAspectRatio = pageSize.width / pageSize.height;
      finalWidth = pageSize.width;
      finalHeight = pageSize.height;
      xOffset = 0;
      yOffset = 0;
      
      // Scale image to cover the entire page (no white margins)
      if (imgAspectRatio > targetPageAspectRatio) {
        // Image is wider - scale by height and crop width
        finalWidth = pageSize.height * imgAspectRatio;
        xOffset = -(finalWidth - pageSize.width) / 2;
      } else {
        // Image is taller - scale by width and crop height
        finalHeight = pageSize.width / imgAspectRatio;
        yOffset = -(finalHeight - pageSize.height) / 2;
      }
    }
    
    // Add image to PDF
    const imgData = canvas.toDataURL('image/png', 1.0);
    
    pdf.addImage(
      imgData,
      'PNG',
      xOffset,
      yOffset,
      finalWidth,
      finalHeight,
      undefined,
      'FAST'
    );

    // Add bleed marks if requested
    if (includeBleed) {
      // Get PDF dimensions (already includes bleed from format)
      const pdfWidth = pageSize.width + 2 * bleedSize;
      const pdfHeight = pageSize.height + 2 * bleedSize;
      
      // Set line properties
      pdf.setDrawColor(0, 0, 0); // Black color
      pdf.setLineWidth(pageSize.unit === 'mm' ? 0.1 : 0.005); // Very thin line
      
      // Corner marks - use appropriate units
      const markLength = pageSize.unit === 'mm' ? 5 : 0.2; // 5mm or 0.2 inches
      
      // Draw crop marks as simple lines - offset from edge
      const offset = pageSize.unit === 'mm' ? 1 : 0.04; // Small offset from edge
      
      // Top-left corner
      pdf.line(bleedSize, offset, bleedSize, offset + markLength); // Vertical line
      pdf.line(offset, bleedSize, offset + markLength, bleedSize); // Horizontal line
      
      // Top-right corner
      pdf.line(pdfWidth - bleedSize, offset, pdfWidth - bleedSize, offset + markLength); // Vertical line
      pdf.line(pdfWidth - offset - markLength, bleedSize, pdfWidth - offset, bleedSize); // Horizontal line
      
      // Bottom-left corner
      pdf.line(bleedSize, pdfHeight - offset - markLength, bleedSize, pdfHeight - offset); // Vertical line
      pdf.line(offset, pdfHeight - bleedSize, offset + markLength, pdfHeight - bleedSize); // Horizontal line
      
      // Bottom-right corner
      pdf.line(pdfWidth - bleedSize, pdfHeight - offset - markLength, pdfWidth - bleedSize, pdfHeight - offset); // Vertical line
      pdf.line(pdfWidth - offset - markLength, pdfHeight - bleedSize, pdfWidth - offset, pdfHeight - bleedSize); // Horizontal line
    }

    // Save PDF
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};